// Kind packs: the thin, per-kind part of the prompt. One contract document for every game
// (contract.md) plus, for the kind the prompt was classified into, a worked example from the
// catalogue, a page of notes, and a starter skeleton the model fills in. Six kinds, not twelve:
// a pack costs an example that must keep passing the soak, so each earns its place.

import type { KindId, StyleId } from "./spec.js";

export interface KindPack {
  id: KindId;
  title: string;
  /** When to pick it, in the classifier's terms: mechanics, camera, input; never theme. */
  pick: string;
  /** Catalogue slug whose game.js is the worked example. */
  example: string;
  defaultStyle: StyleId;
  /** Idioms and gotchas specific to the kind. Markdown. */
  notes: string;
  /** A starter game.js with the structure in place and the rules left to write. */
  skeleton: string;
}

const COMMON_SKELETON_HEAD = `import { defineEntity } from "#runtime";

export const meta = {
  name: "TITLE",
  world: { w: 3000, h: 2000 },
  camera: "follow",
  players: { max: 32 },
  tickRate: 20,
  score: "score",
  controls: "A does X; B does Y.",
};

export const inputSchema = { tx: "f32", ty: "f32", a: "bool", b: "bool" };

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
`;

export const KIND_PACKS: KindPack[] = [
  {
    id: "grow",
    title: "Grow and eat",
    pick: "Top-down, free movement toward the pointer, things to eat scattered about, players eat smaller players or pickups and grow; a leaderboard by size. Agar-likes, snake-likes, feeding frenzies.",
    example: "agar",
    defaultStyle: "shapes",
    notes: `- Food is \`static: true\` and despawned when eaten; spawn a replacement somewhere else so the count stays constant.
- Bigger is slower: speed falls with \`r\`, or big players never get caught and the room stalls.
- Eating a player resets them in place to the starting size; never despawn a player entity.
- The leaderboard field is the size or a score; name it in \`meta.score\`.
- A snake is a head (the player's entity) plus segment entities it owns, each following the one in front; \`onLeave\` lets the runtime remove them.`,
    skeleton:
      COMMON_SKELETON_HEAD +
      `
defineEntity("food", {
  replicated: ["x", "y", "r"],
  static: true,
  spawn(world, _owner, rng) {
    return { x: rng.range(20, world.w - 20), y: rng.range(20, world.h - 20), r: 7 };
  },
  draw(self, ctx) {
    // TODO: a small shape or a sprite
  },
});

defineEntity("player", {
  replicated: ["x", "y", "r", "score"],
  spawn(world, _owner, rng) {
    return { x: rng.range(100, world.w - 100), y: rng.range(100, world.h - 100), r: 22, score: 0 };
  },
  tick(self, world, input, dt, rng) {
    if (input) {
      // TODO: move toward input.tx, input.ty; speed falls with self.r
    }
    self.x = clamp(self.x, self.r, world.w - self.r);
    self.y = clamp(self.y, self.r, world.h - self.r);
    for (const e of world.query(self.x, self.y, self.r)) {
      // TODO: eat food (despawn it, spawn another, grow) and smaller players (reset them in place)
    }
  },
  draw(self, ctx, world) {
    // TODO: the body, then the name: world.name(self.owner)
  },
});

export function init(world, rng) {
  for (let i = 0; i < 400; i++) world.spawn("food");
}

export function onJoin(world, playerId, rng) {
  world.spawn("player", playerId);
}

export function onLeave(world, playerId) {}
`,
  },
  {
    id: "side",
    title: "Side view with gravity",
    pick: "A side-on world where things fall: platformers, brawlers, climbers, runners, boss fights seen from the side. The pointer's x is the run direction, A jumps, B acts. Platforms are static blocks.",
    example: "tethered",
    defaultStyle: "pixel-18",
    notes: `- Gravity in \`tick\`: \`self.vy += G * dt\`, then move, then resolve collisions with platforms found by \`world.query\`.
- Platforms, ledges and walls are \`static: true\` entities; a level is a few dozen of them spawned in \`init\`, not thousands.
- "Grounded" is a server-only field set during collision; A jumps only when grounded, with a short coyote time.
- The run direction is \`input.tx\` relative to \`self.x\`; \`input.ty\` is for aiming if the game shoots.
- Falling off the bottom respawns in place at a spawn point; keep the id and owner.
- The camera follows the player; keep the world no wider than about 4000 so a race stays readable.
- Enemies and hazards scale with \`world.players().length\` from the world-level \`tick\`.`,
    skeleton:
      COMMON_SKELETON_HEAD +
      `
const G = 1800;
const RUN = 320;
const JUMP = 720;

defineEntity("block", {
  replicated: ["x", "y", "r", "w", "h"],
  static: true,
  spawn(_world, _owner, _rng) {
    return { x: 0, y: 0, r: 40, w: 80, h: 40 }; // overridden per block in init
  },
  draw(self, ctx) {
    // TODO: a rectangle self.w by self.h centred on self.x, self.y, or sprites tiled across it
  },
});

defineEntity("player", {
  replicated: ["x", "y", "r", "f", "score"],
  spawn(world, _owner, rng) {
    return { x: rng.range(100, 400), y: 200, r: 20, vx: 0, vy: 0, f: 1, grounded: false, score: 0 };
  },
  tick(self, world, input, dt, rng) {
    if (input) {
      const dir = input.tx < self.x - 8 ? -1 : input.tx > self.x + 8 ? 1 : 0;
      self.vx = dir * RUN;
      if (dir) self.f = dir;
      if (input.a && self.grounded) self.vy = -JUMP;
    } else self.vx = 0;
    self.vy += G * dt;
    self.x += self.vx * dt;
    self.y += self.vy * dt;
    self.grounded = false;
    for (const b of world.query(self.x, self.y, self.r + 60)) {
      if (b.type !== "block") continue;
      // TODO: axis-aligned collision: push out of the block; landing on top sets grounded and vy = 0
    }
    self.x = clamp(self.x, self.r, world.w - self.r);
    if (self.y > world.h + 200) {
      // TODO: respawn in place
    }
  },
  draw(self, ctx, world) {
    // TODO: body facing self.f, then the name
  },
});

export function init(world, rng) {
  // TODO: the ground and a few dozen platforms: world.spawn("block", undefined, { x, y, w, h, r: Math.max(w, h) / 2 })
}

export function onJoin(world, playerId, rng) {
  world.spawn("player", playerId);
}

export function onLeave(world, playerId) {}
`,
  },
  {
    id: "dig",
    title: "Dig and build on a grid",
    pick: "A world made of blocks on a grid that players dig out and place; side view with gravity or top-down. Mining, sandbox, terraria-likes.",
    example: "burrow",
    defaultStyle: "pixel-18",
    notes: `- One static entity per block, spawned in \`init\` from a grid; keep it to a few hundred to a thousand blocks and let the state budget be the limit.
- Digging despawns a block; placing spawns one, snapped to the grid, only where nothing is.
- The pointer picks the cell: round \`input.tx\`, \`input.ty\` to the grid, and only act within reach.
- Carried blocks and gold are fields on the player entity, replicated if others should see them.
- Shared world: what one player digs stays dug for everyone; there is no per-player world.`,
    skeleton:
      COMMON_SKELETON_HEAD +
      `
const CELL = 40;
const COLS = 60;
const ROWS = 40;

defineEntity("block", {
  replicated: ["x", "y", "r", "kind"],
  static: true,
  spawn(_world, _owner, _rng) {
    return { x: 0, y: 0, r: CELL / 2, kind: 1 }; // overridden per block in init
  },
  draw(self, ctx) {
    // TODO: a square by kind, or a tile sprite
  },
});

defineEntity("player", {
  replicated: ["x", "y", "r", "carry", "score"],
  spawn(world, _owner, rng) {
    return { x: rng.range(100, world.w - 100), y: 80, r: 16, vy: 0, grounded: false, carry: 0, score: 0, cool: 0 };
  },
  tick(self, world, input, dt, rng) {
    self.cool -= dt;
    // TODO: move, gravity, collide with blocks via world.query
    if (input && input.b && self.cool <= 0) {
      const cx = Math.floor(input.tx / CELL) * CELL + CELL / 2;
      const cy = Math.floor(input.ty / CELL) * CELL + CELL / 2;
      // TODO: if a block is there and within reach, dig it; else if the cell is empty and self.carry > 0, place one
      self.cool = 0.25;
    }
  },
  draw(self, ctx, world) {
    // TODO
  },
});

export function init(world, rng) {
  for (let j = 8; j < ROWS; j++) for (let i = 0; i < COLS; i++) {
    // TODO: choose a kind by depth and rng; skip some cells for caves
    world.spawn("block", undefined, { x: i * CELL + CELL / 2, y: j * CELL + CELL / 2 });
  }
}

export function onJoin(world, playerId, rng) {
  world.spawn("player", playerId);
}

export function onLeave(world, playerId) {}
`,
  },
  {
    id: "stations",
    title: "Top-down walk-about with stations and NPCs",
    pick: "Top-down, walk to places and act on them: farms, kitchens, villages, story worlds with NPCs, co-op puzzles, survival with resources and building. The pointer is where to walk, B works the nearest thing.",
    example: "allotment",
    defaultStyle: "pixel-16",
    notes: `- Stations (plots, counters, doors, fires) are entities with a replicated stage; players act on the nearest one within reach when B is pressed, with a short cooldown.
- NPCs wander with a target and a timer, both server-only fields; they never read input.
- Shared state is just entities: a door anyone opens stays open for everyone; a plate that needs two players counts \`world.query\` hits.
- A day clock, waves at night or a delivery timer live in the world-level \`tick\` keyed on \`world.time\`.
- Scenery (trees, walls, paths) is \`static: true\` on layer -1; use the Tiny Town sprites.
- Progress a player carries is a replicated field on their entity, so others can see it.`,
    skeleton:
      COMMON_SKELETON_HEAD +
      `
const REACH = 60;

defineEntity("station", {
  replicated: ["x", "y", "r", "stage"],
  spawn(_world, _owner, _rng) {
    return { x: 0, y: 0, r: 30, stage: 0, t: 0 }; // overridden in init
  },
  tick(self, world, _input, dt, rng) {
    // TODO: stages that advance with time, if any
  },
  draw(self, ctx, world) {
    // TODO: by stage; sprites from the pixel-16 library
  },
});

defineEntity("npc", {
  replicated: ["x", "y", "r", "f"],
  spawn(world, _owner, rng) {
    return { x: rng.range(80, world.w - 80), y: rng.range(80, world.h - 80), r: 14, f: 1, tx: 0, ty: 0, t: 0 };
  },
  tick(self, world, _input, dt, rng) {
    self.t -= dt;
    if (self.t <= 0) {
      self.tx = clamp(self.x + rng.range(-200, 200), 40, world.w - 40);
      self.ty = clamp(self.y + rng.range(-200, 200), 40, world.h - 40);
      self.t = rng.range(2, 5);
    }
    // TODO: step toward tx, ty
  },
  draw(self, ctx) {
    // TODO
  },
});

defineEntity("player", {
  replicated: ["x", "y", "r", "c", "score"],
  spawn(world, _owner, rng) {
    return { x: rng.range(100, world.w - 100), y: rng.range(100, world.h - 100), r: 18, c: rng.int(8), score: 0, cool: 0 };
  },
  tick(self, world, input, dt, rng) {
    self.cool -= dt;
    if (input) {
      // TODO: walk toward input.tx, input.ty (A hurries)
      if (input.b && self.cool <= 0) {
        let nearest = null;
        let best = REACH;
        for (const e of world.query(self.x, self.y, REACH)) {
          if (e.type !== "station") continue;
          const d = Math.hypot(e.x - self.x, e.y - self.y);
          if (d < best) { best = d; nearest = e; }
        }
        if (nearest) {
          // TODO: act on nearest by its stage
          self.cool = 0.35;
        }
      }
    }
    self.x = clamp(self.x, self.r, world.w - self.r);
    self.y = clamp(self.y, self.r, world.h - self.r);
  },
  draw(self, ctx, world) {
    // TODO: a villager sprite or a shape, then the name
  },
});

export function init(world, rng) {
  // TODO: spawn the stations in a layout, scenery, and a few npcs
}

export function onJoin(world, playerId, rng) {
  world.spawn("player", playerId);
}

export function onLeave(world, playerId) {}
`,
  },
  {
    id: "shared",
    title: "One shared object everyone operates",
    pick: "Players crew or contest one object together: a ship with stations, a ball or puck, a flag, a raft. The object has its own tick; players attach to it or push it.",
    example: "crewed",
    defaultStyle: "vector",
    notes: `- The shared object is one entity with no owner, spawned in \`init\`, with its own \`tick\`; it reads the players attached to it through fields holding their ids.
- A station is a field on the object holding the player id seated there, or 0; B takes or leaves it; \`onLeave\` must clear any seat the departed player held.
- When the object moves, move its crew with it in the object's tick, before the crew tick, so nobody is left behind.
- A ball or puck is the same idea: one entity, pushed by players who overlap it, with friction and bounces off the world edge.
- Scores are shared: a field on the object, named in \`meta.score\` on players if you want a leaderboard, or a scoreboard entity drawn in a corner.`,
    skeleton:
      COMMON_SKELETON_HEAD +
      `
defineEntity("vessel", {
  replicated: ["x", "y", "r", "a", "hp"],
  spawn(world, _owner, _rng) {
    return { x: world.w / 2, y: world.h / 2, r: 90, a: 0, hp: 100, vx: 0, vy: 0, helm: "", gun: "" };
  },
  tick(self, world, _input, dt, rng) {
    // TODO: read the helm seat's input through world.playerEntity(self.helm) and its fields; move; carry seated players along
  },
  draw(self, ctx) {
    // TODO
  },
});

defineEntity("player", {
  replicated: ["x", "y", "r", "seat", "score"],
  spawn(world, _owner, rng) {
    return { x: world.w / 2 + rng.range(-40, 40), y: world.h / 2 + rng.range(-40, 40), r: 14, seat: "", score: 0, cool: 0, tx: 0, ty: 0 };
  },
  tick(self, world, input, dt, rng) {
    self.cool -= dt;
    if (input) {
      self.tx = input.tx;
      self.ty = input.ty;
      if (input.b && self.cool <= 0) {
        // TODO: take the nearest free seat on the vessel, or leave the current one
        self.cool = 0.4;
      }
      if (!self.seat) {
        // TODO: walk toward tx, ty within the vessel
      }
    }
  },
  draw(self, ctx, world) {
    // TODO
  },
});

export function init(world, rng) {
  world.spawn("vessel");
}

export function onJoin(world, playerId, rng) {
  world.spawn("player", playerId);
}

export function onLeave(world, playerId) {
  for (const v of world.ofType("vessel")) {
    if (v.helm === playerId) v.helm = "";
    if (v.gun === playerId) v.gun = "";
  }
}
`,
  },
  {
    id: "chase",
    title: "Tag, chase and infection",
    pick: "Top-down, players hunt other players: tag, infection, hide and seek, predators and prey, zombies. Roles change on contact; the other players are the whole game.",
    example: "agar",
    defaultStyle: "pixel-16",
    notes: `- A role is a replicated number on the player entity (\`it: 0 | 1\`, or \`team\`); contact is a \`world.query\` in the tagger's tick against entities with \`owner !== self.owner\`.
- Someone must always be it: in the world-level \`tick\`, if nobody is, pick one with \`rng\` from \`world.players()\`; when the tagged player leaves, pick again.
- Infection flows one way; when everyone has turned, reset the roles in place and carry on, since there are no rounds.
- Give the chaser a small speed edge and the chased a dash on A with a cooldown, or nothing ever resolves.
- Score is time survived or tags made, a replicated field on the player entity named in \`meta.score\`.
- The example is Blobs because the movement and the culling are the same; swap eating for tagging.`,
    skeleton:
      COMMON_SKELETON_HEAD +
      `
const SPEED = 260;

defineEntity("player", {
  replicated: ["x", "y", "r", "it", "score"],
  spawn(world, _owner, rng) {
    return { x: rng.range(100, world.w - 100), y: rng.range(100, world.h - 100), r: 20, it: 0, score: 0, dash: 0, safe: 0 };
  },
  tick(self, world, input, dt, rng) {
    self.dash -= dt;
    self.safe -= dt;
    if (input) {
      const dx = input.tx - self.x;
      const dy = input.ty - self.y;
      const d = Math.hypot(dx, dy);
      const speed = SPEED * (self.it ? 1.08 : 1) * (input.a && self.dash <= 0 ? 1.8 : 1);
      if (d > 2) {
        const s = Math.min(d, speed * dt);
        self.x += (dx / d) * s;
        self.y += (dy / d) * s;
      }
      // TODO: start the dash cooldown when A is used
    }
    self.x = clamp(self.x, self.r, world.w - self.r);
    self.y = clamp(self.y, self.r, world.h - self.r);
    if (self.it) {
      for (const e of world.query(self.x, self.y, self.r + 4)) {
        if (e.type === "player" && e.owner !== self.owner && e.safe <= 0) {
          // TODO: pass the role: e.it = 1, self.it = 0, self.safe = 1.5, self.score += 1
          break;
        }
      }
    } else self.score += dt;
  },
  draw(self, ctx, world) {
    // TODO: colour by self.it, then the name
  },
});

export function tick(world, dt, rng) {
  const players = world.players();
  if (players.length < 2) return;
  if (!players.some((p) => world.playerEntity(p)?.it)) {
    const chosen = world.playerEntity(players[rng.int(players.length)]);
    if (chosen) chosen.it = 1;
  }
}

export function init(world, rng) {
  // TODO: a few static obstacles to hide behind
}

export function onJoin(world, playerId, rng) {
  world.spawn("player", playerId);
}

export function onLeave(world, playerId) {}
`,
  },
];

export function kindPack(id: string): KindPack | undefined {
  return KIND_PACKS.find((k) => k.id === id);
}
